import React, { useState, useRef } from 'react';
import { Upload, Download, Type, Briefcase, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const InvitationSystem = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    photo: null,
    name: '',
    title: '',
    photoPreview: null
  });
  const [finalInvitation, setFinalInvitation] = useState(null);
  const canvasRef = useRef(null);

  // Load templates từ localStorage
  React.useEffect(() => {
    const saved = localStorage.getItem('templates');
    if (saved) {
      setTemplates(JSON.parse(saved));
    }
  }, []);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({
          ...formData,
          photo: event.target.result,
          photoPreview: event.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const generateInvitation = () => {
    if (!selectedTemplate || !formData.photo || !formData.name) {
      alert('Vui lòng chọn mẫu, upload ảnh và nhập tên!');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const template = selectedTemplate;

    const exactWidth = Math.round(template.width);
    const exactHeight = Math.round(template.height);

    canvas.width = exactWidth;
    canvas.height = exactHeight;
    canvas.style.width = `${exactWidth}px`;
    canvas.style.height = `${exactHeight}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, exactWidth, exactHeight);

    const bgImg = new Image();
    bgImg.onload = () => {
      ctx.drawImage(bgImg, 0, 0, exactWidth, exactHeight);

      const photoImg = new Image();
      photoImg.onload = () => {
        const { x, y, width, height, shape, borderRadius, fit } = template.fields.photo;

        ctx.save();
        ctx.beginPath();
        if (shape === 'circle') {
          ctx.arc(x + width / 2, y + height / 2, width / 2, 0, Math.PI * 2);
        } else if (shape === 'rounded') {
          ctx.roundRect(x, y, width, height, borderRadius || 20);
        } else {
          ctx.rect(x, y, width, height);
        }
        ctx.clip();

        const targetRatio = width / height;
        const sourceRatio = photoImg.width / photoImg.height;

        let drawX = x;
        let drawY = y;
        let drawWidth = width;
        let drawHeight = height;

        if (fit === 'contain') {
          if (sourceRatio > targetRatio) {
            drawHeight = width / sourceRatio;
            drawY = y + (height - drawHeight) / 2;
          } else {
            drawWidth = height * sourceRatio;
            drawX = x + (width - drawWidth) / 2;
          }
          ctx.drawImage(photoImg, drawX, drawY, drawWidth, drawHeight);
        } else {
          let sourceWidth = photoImg.width;
          let sourceHeight = photoImg.height;
          let sourceX = 0;
          let sourceY = 0;

          if (sourceRatio > targetRatio) {
            sourceWidth = photoImg.height * targetRatio;
            sourceX = (photoImg.width - sourceWidth) / 2;
          } else {
            sourceHeight = photoImg.width / targetRatio;
            sourceY = (photoImg.height - sourceHeight) / 2;
          }

          ctx.drawImage(photoImg, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
        }

        ctx.restore();

        // Render Name
        const nameField = template.fields.name;
        ctx.font = `${nameField.fontWeight || 'bold'} ${nameField.fontSize}px ${nameField.fontFamily || 'Arial'}`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = nameField.align || 'center';
        ctx.fillStyle = nameField.color || '#000000';
        ctx.fillText(formData.name || 'Họ và tên', nameField.x, nameField.y);

        // Render Title nếu có
        if (formData.title) {
          const titleField = template.fields.title;
          ctx.font = `${titleField.fontWeight || 'normal'} ${titleField.fontSize}px ${titleField.fontFamily || 'Arial'}`;
          ctx.textBaseline = 'middle';
          ctx.textAlign = titleField.align || 'center';
          ctx.fillStyle = titleField.color || '#666666';
          ctx.fillText(formData.title || 'Chức vụ', titleField.x, titleField.y);
        }

        setFinalInvitation(canvas.toDataURL('image/png', 1.0));
      };
      photoImg.src = formData.photo;
    };
    bgImg.src = template.imageUrl;
  };

  const downloadInvitation = () => {
    const link = document.createElement('a');
    link.download = `thiep-moi-${formData.name.replace(/\s+/g, '-')}.png`;
    link.href = finalInvitation;
    link.click();
  };

  const resetForm = () => {
    setFormData({ photo: null, name: '', title: '', photoPreview: null });
    setFinalInvitation(null);
    setSelectedTemplate(null);
  };

  const navigate = useNavigate();

  if (finalInvitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h2 className="text-3xl font-bold text-center mb-6 text-gray-800">✨ Thiệp mời của bạn</h2>
            <div className="border-4 border-purple-200 rounded-xl overflow-hidden mb-6">
              <img src={finalInvitation} alt="Thiệp" className="w-full" />
            </div>
            <div className="flex gap-4">
              <button onClick={downloadInvitation} className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition flex items-center justify-center gap-2">
                <Download className="w-6 h-6" />
                Tải xuống
              </button>
              <button onClick={resetForm} className="flex-1 bg-gray-200 text-gray-800 py-4 rounded-xl font-bold text-lg hover:bg-gray-300 transition">
                Tạo mới
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedTemplate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="max-w-6xl mx-auto pt-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600 mb-2">
              🎉 Year End Party 2026
            </h1>
            <p className="text-gray-600 text-lg">Chọn mẫu thiệp bạn yêu thích</p>
                {/* <button 
                onClick={() => navigate('/admin')}
                className="mt-4 px-4 py-2 bg-white rounded-full shadow-md hover:shadow-lg transition text-sm"
                >
                <Settings className="inline w-4 h-4 mr-1" />
                Quản lý mẫu
                </button>  */}
          </div>

          {templates.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <p className="text-xl text-gray-600 mb-4">Chưa có mẫu thiệp nào</p>
              <button 
                onClick={() => navigate('/admin')}
                className="bg-blue-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-600 transition"
              >
                Thêm mẫu thiệp
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300">
                  <div className="relative">
                    <img src={template.imageUrl} alt={template.name} className="w-full h-64 object-cover" />
                    <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full text-sm font-bold text-gray-700 shadow-lg">
                      {template.orientation === 'portrait' ? '📱 Dọc' : '💻 Ngang'}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{template.name}</h3>
                    <p className="text-gray-600 mb-4">{template.width} x {template.height}px</p>
                    <div className="flex gap-3">
                      <button onClick={() => setSelectedTemplate(template)} className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition">
                        Chọn mẫu này
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-5xl mx-auto pt-8">
        <button onClick={() => setSelectedTemplate(null)} className="mb-6 px-6 py-2 bg-white rounded-full shadow-md hover:shadow-lg transition text-gray-700 font-medium">
          ← Đổi mẫu khác
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-bold mb-4 text-gray-800">👁️ Xem trước mẫu</h3>
            <div className="border-4 border-blue-200 rounded-xl overflow-hidden bg-gray-50">
              <img src={selectedTemplate.imageUrl} alt="Preview" className="w-full" />
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">{selectedTemplate.name}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-bold mb-6 text-gray-800">✍️ Điền thông tin</h3>
            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <Upload className="w-5 h-5" />
                  Ảnh của bạn
                </label>
                <div className="border-2 border-dashed border-blue-300 rounded-xl p-6 text-center hover:border-blue-500 transition">
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="photo-upload" />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    {formData.photoPreview ? (
                      <img src={formData.photoPreview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-gray-600">Click để chọn ảnh</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <Type className="w-5 h-5" />
                  Họ và tên
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nguyễn Văn A"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-lg"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <Briefcase className="w-5 h-5" />
                  Chức vụ (không bắt buộc)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Giám đốc, Nhân viên, v.v."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition text-lg"
                />
              </div>
              <button onClick={generateInvitation} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition transform hover:scale-105">
                🎨 Tạo thiệp ngay
              </button>
            </div>
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default InvitationSystem;